import axios from "axios";
import config from "../config";
import store from "../redux/store";
import { logOutAction } from "../redux/auth";

/**
 * Centralized Axios instance with the backend base URL pre-configured.
 *
 * Usage:
 *   import api from "../services/axiosInstance";      // from top-level
 *   import api from "../../services/axiosInstance";    // from sub-directories
 *
 *   const { data } = await api.get("/auth/login");
 *   const { data } = await api.post("/user", payload);
 *
 * All service files should import this instead of raw `axios`.
 */
const api = axios.create({
  baseURL: config.backend,
  timeout: 30000, // 30-second default timeout
});

// ── Request interceptor: attach JWT token to every request ──────────────
api.interceptors.request.use(
  (requestConfig) => {
    const { token } = store.getState().auth;
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: handle 401 (expired/invalid token) ────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const { isAuth } = store.getState().auth;
      // Only auto-logout if the user was previously authenticated
      // (avoids clearing state on a failed login attempt)
      if (isAuth) {
        store.dispatch(logOutAction());
        // Redirect to the login page
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

export default api;
